import * as React from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { usePdfFromHtml } from "@/hooks/usePdfFromHtml";
import { BoletoBarcode } from "./BoletoBarcode";

// ====== Tipos (os mesmos que você já tinha) ======
interface Address { street: string; city: string; state: string; zipCode: string; country: string; }
interface Party { name: string; document: string; email: string; phone: string; address: Address; }
interface InvoiceItem { description: string; quantity: number; unitPrice: number; total: number; }
interface Totals { subtotal: number; tax: number; total: number; }
interface PaymentInfo { method: string; dueDate: string; instructions: string; }
interface InvoiceMeta { number: string; issueDate: string; dueDate: string; currency: string; }
interface InvoiceData {
  meta: InvoiceMeta; issuer: Party; customer: Party; items: InvoiceItem[];
  totals: Totals; paymentInfo: PaymentInfo; notes: string;
  pixQrCodeBase64?: string; boletoUrl?: string;
  payment?: { pixPayload?: string; pixKey?: string; boletoLine?: string; boletoBarcode?: string; };
}
interface InvoiceProps { data?: Partial<InvoiceData>; logoUrl?: string; }

// ====== Utils ======
const fmtBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const fmtDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = typeof dateStr === 'string' && dateStr.includes('/') ? dateStr : new Date(dateStr).toLocaleDateString('pt-BR');
  return date;
};

const ensureAddress = (addr?: Partial<Address>): Address => ({
  street: addr?.street || '', city: addr?.city || '', state: addr?.state || '',
  zipCode: addr?.zipCode || '', country: addr?.country || 'Brasil'
});
const ensureParty = (party?: Partial<Party>): Party => ({
  name: party?.name || '', document: party?.document || '', email: party?.email || '',
  phone: party?.phone || '', address: ensureAddress(party?.address)
});
const addrLine = (a: Address): string => {
  const parts: string[] = [];
  if (a.street) parts.push(a.street);
  if (a.city && a.state) parts.push(`${a.city} - ${a.state.substring(0,2).toUpperCase()}`);
  return parts.join(" - ");
};
const normalizeInvoiceData = (input?: Partial<InvoiceData>): InvoiceData => {
  const meta = {
    number: input?.meta?.number || 'INV-001',
    issueDate: input?.meta?.issueDate || new Date().toISOString(),
    dueDate: input?.meta?.dueDate || new Date(Date.now() + 30*24*60*60*1000).toISOString(),
    currency: input?.meta?.currency || 'BRL'
  };
  const issuer = ensureParty(input?.issuer);
  const customer = ensureParty(input?.customer);
  const items = input?.items || [];
  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const tax = 0;
  const total = subtotal;
  const totals = { subtotal, tax, total };
  const paymentInfo = {
    method: input?.paymentInfo?.method || 'PIX',
    dueDate: input?.paymentInfo?.dueDate || meta.dueDate,
    instructions: input?.paymentInfo?.instructions || 'Pagamento via PIX'
  };
  return {
    meta, issuer, customer, items, totals, paymentInfo,
    notes: input?.notes || '', payment: input?.payment,
    pixQrCodeBase64: input?.pixQrCodeBase64, boletoUrl: input?.boletoUrl
  };
};


export default function InvoiceAzure({ data, logoUrl }: InvoiceProps) {
  const invoice = normalizeInvoiceData(data);
  const { generatePdfFromElement } = usePdfFromHtml();

  const handleDownloadPdf = React.useCallback(async () => {
    await generatePdfFromElement('invoice-a4', {
      filename: `invoice-${invoice.meta.number || 'azure'}.pdf`,
      mode: 'fit-a4',
      paddingPx: 0,
      background: '#ffffff',
    });
  }, [generatePdfFromElement, invoice.meta.number]);

  return (
    <div className="min-h-screen bg-slate-50 p-4">

      {/* Botão Download PDF */}
      <div className="no-print mb-3 flex justify-end">
        <Button onClick={handleDownloadPdf} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Baixar PDF
        </Button>
      </div>

      {/* ====== WRAPPER A4 ====== */}
      <div id="invoice-a4" className="a4-page a4-shadow">
        <div className="a4-content">
          {/* Header */}
          <div className="bg-blue-600 text-white -mt-[45px] -mx-[45px] p-4 mb-4 no-break">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-8 mb-2 filter brightness-0 invert flex-shrink-0" />
              ) : (
                <div className="text-xl font-bold mb-2 flex-shrink-0">PortalONE Suite</div>
              )}
              <h1 className="text-2xl font-light whitespace-nowrap">Fatura</h1>
              <p className="text-blue-100 mt-2 break-words">#{invoice.meta.number}</p>
            </div>
            <div className="text-right flex-shrink-0 max-w-[250px]">
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-sm text-blue-100 whitespace-nowrap">Data de Emissão</p>
                <p className="text-lg font-semibold whitespace-nowrap">{fmtDate(invoice.meta.issueDate)}</p>
                <p className="text-sm text-blue-100 mt-2 whitespace-nowrap">Vencimento</p>
                <p className="text-lg font-semibold whitespace-nowrap">{fmtDate(invoice.meta.dueDate)}</p>
              </div>
            </div>
          </div>
        </div>

          {/* Content Area */}
          <div>
          {/* Emitente / Cliente */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-600 no-break">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Emitente
              </h3>
              <div className="space-y-2 text-gray-700">
                <p className="font-medium">{invoice.issuer.name}</p>
                <p className="text-sm">CNPJ: {invoice.issuer.document}</p>
                <p className="text-sm">{invoice.issuer.email}</p>
                <p className="text-sm">{invoice.issuer.phone}</p>
                <div className="text-sm text-gray-600">{addrLine(invoice.issuer.address)}</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-gray-400 no-break">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                Cliente
              </h3>
              <div className="space-y-2 text-gray-700">
                <p className="font-medium">{invoice.customer.name}</p>
                <p className="text-sm">CNPJ: {invoice.customer.document}</p>
                <p className="text-sm">{invoice.customer.email}</p>
                <p className="text-sm">{invoice.customer.phone}</p>
                <div className="text-sm text-gray-600">{addrLine(invoice.customer.address)}</div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Itens da Fatura</h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="a4-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 col-desc">Descrição</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 col-qtd">Qtd</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 col-unit">Valor Unit.</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 col-total">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-800 text-xs">{item.description}</td>
                      <td className="px-4 py-2 text-right text-gray-700 text-xs">{item.quantity}</td>
                      <td className="px-4 py-2 text-right text-gray-700 text-xs">{fmtBRL(item.unitPrice)}</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-800 text-xs">{fmtBRL(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
                {/* Se quiser rodapé fixo por página, use tfoot */}
                {/* <tfoot><tr><td colSpan={4}>...</td></tr></tfoot> */}
              </table>
            </div>
          </div>

          {/* Totais */}
          <div className="flex justify-end mb-4 no-break">
            <div className="w-80">
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span>{fmtBRL(invoice.totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Impostos:</span>
                  <span>{fmtBRL(invoice.totals.tax)}</span>
                </div>
                <div className="border-t border-gray-300 pt-3">
                  <div className="flex justify-between text-xl font-semibold text-blue-600">
                    <span>Total:</span>
                    <span>{fmtBRL(invoice.totals.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagamento */}
          <div className="mb-4 page-avoid-break">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Informações de Pagamento</h3>
              <p className="text-sm text-blue-700">{invoice.paymentInfo.instructions}</p>
              
              {invoice.payment?.pixPayload && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* QR Code - Maior */}
                  {invoice.pixQrCodeBase64 ? (
                    <div className="flex justify-center">
                      <img 
                        src={invoice.pixQrCodeBase64.startsWith('data:image') ? invoice.pixQrCodeBase64 : `data:image/png;base64,${invoice.pixQrCodeBase64}`}
                        alt="QR Code PIX" 
                        className="invoice-qr border rounded-lg p-2 w-48 h-48 object-contain bg-white"
                      />
                    </div>
                  ) : (
                    <div className="border rounded-lg p-2 h-48 grid place-items-center text-xs text-blue-500">
                      QR PIX não disponível
                    </div>
                  )}
                  
                  {/* PIX Copia e Cola */}
                  <div className="md:col-span-2">
                    <div className="font-medium text-sm mb-2">PIX Copia e Cola:</div>
                    <div className="p-2 bg-white rounded border text-xs font-mono break-all max-h-32 overflow-auto text-gray-900">
                      {invoice.payment.pixPayload}
                    </div>
                    {invoice.payment.pixKey && (
                      <div className="mt-3 text-sm">
                        <span className="font-medium">Chave PIX:</span> {invoice.payment.pixKey}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(invoice.payment?.boletoBarcode || invoice.payment?.boletoLine) && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Informações de Pagamento - Boleto
                  </h4>
                  <BoletoBarcode
                    barcode={invoice.payment.boletoBarcode}
                    digitableLine={invoice.payment.boletoLine}
                    boletoUrl={invoice.boletoUrl}
                  />
                </div>
              )}
            </div>
          </div>

          </div>

          {/* Rodapé */}
          <footer className="mt-4 pt-2 border-t page-avoid-break">
          <div className="text-center text-[10px] text-gray-400 space-y-0.5">
            <p>Esta fatura não substitui a emissão de documento fiscal quando obrigatório (NF-e/NFS-e).</p>
            <p>Multa de 2% e juros de 1% a.m. após o vencimento (se aplicável).</p>
            <p className="mt-1">Fatura gerada automaticamente pelo sistema PortalONE. Para dúvidas entre em contato conosco.</p>
            <div className="page-number mt-1" />
            <div className="page-number mt-1" />
          </div>
          </footer>
        </div>
      </div>

    </div>
  );
}